import { Tabs } from 'antd';
import React, { forwardRef } from 'react';
import styled from 'styled-components';

type Props = {
  tabs: string[];
  selectedTabIndex: number;
  allowSliding?: boolean;
  setSelectedTabIndex: (index: number) => void;
  className?: string;
};

const ButtonsContainer = styled.div`
  width: 100%;
  display: inline-flex;
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  padding-bottom: 8px;
  transition: transform 0.3s ease-out;
`;

const TabButton = styled.p<{ $isSelected: boolean }>`
  width: 100%;
  text-align: center;
  font-weight: ${(props) => (props.$isSelected ? '600' : 'inherit')};
  cursor: pointer;
`;

const GreyLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  border: 1px solid ${(props) => props.theme.colors.grey};
  background-color: ${(props) => props.theme.colors.grey};
  border-radius: ${(props) => props.theme.borderRadius};
  width: 100%;
`;

const OrangeLine = styled.div<{
  nTabs: number;
  selectedTabIndex: number;
}>`
  top: 0;
  left: 0;
  z-index: 5;
  position: absolute;
  border: 1px solid ${(props) => props.theme.colors.primary};
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};
  transition: left 400ms;

  width: calc(100% / ${(props) => props.nTabs});
  left: calc(
    calc(100% / ${(props) => props.nTabs}) *
      ${(props) => props.selectedTabIndex}
  );
`;

const MenuDivider = styled(
  ({
    tabs,
    selectedTabIndex,
    className,
  }: {
    tabs: string[];
    selectedTabIndex: number;
    className?: string;
  }): JSX.Element => {
    return (
      <div className={className}>
        <GreyLine />
        <OrangeLine nTabs={tabs.length} selectedTabIndex={selectedTabIndex} />
      </div>
    );
  }
)`
  position: absolute;
  bottom: 2px;
  width: 100%;
`;

const TabSelector = forwardRef<HTMLDivElement, Props>(
  (
    { tabs, selectedTabIndex, setSelectedTabIndex, allowSliding, className },
    ref
  ) => {
    return (
      <div className={className} ref={ref}>
        {allowSliding ? (
          <Tabs
            items={tabs.map((t, index) => ({ label: t, key: index + '' }))}
            centered
            activeKey={selectedTabIndex + ''}
            onTabClick={(activeKey) =>
              setSelectedTabIndex(parseInt(activeKey, 10))
            }
          />
        ) : (
          <ButtonsContainer>
            {tabs.map((tab, index) => {
              let isSelected = false;
              if (index === selectedTabIndex) {
                isSelected = true;
              }
              return (
                <TabButton
                  key={tab}
                  as="p"
                  $isSelected={isSelected}
                  onClick={() => {
                    setSelectedTabIndex(index);
                  }}
                >
                  {tab}
                </TabButton>
              );
            })}
            <MenuDivider tabs={tabs} selectedTabIndex={selectedTabIndex} />
          </ButtonsContainer>
        )}
      </div>
    );
  }
);

TabSelector.displayName = 'TabSelector';

export default styled(TabSelector)`
  position: relative;
  padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-top: 16px;
  padding-bottom: 16px;
  background-color: white;
  ${(props) => props.theme.font.p1}

  .ant-tabs-tab {
    padding-left: 8px;
    padding-right: 8px;
    ${(props) => props.theme.font.p1}

    + .ant-tabs-tab {
      margin-left: 16px;
    }

    &.ant-tabs-tab-active .ant-tabs-tab-btn {
      font-weight: 600;
      text-shadow: none;
    }

    .ant-tabs-tab-btn {
      color: black;

      &:focus,
      &:active {
        color: black;
      }
    }
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    padding-left: 0;
    padding-right: 0;
  }
`;
